import { useEffect, useMemo, useState } from 'react';
import type { CwOpportunitySalesRepAliasDto } from '@catalyst-psa/shared';

import { Button } from '../../components/ui/Button.js';
import { useCwOpportunitySalesRepAliases, useSetCwOpportunitySalesRepAliases } from './api.js';

interface AliasRow {
  id: string;
  cwSalesRepName: string;
  memberId: string;
}

let aliasSeq = 0;
function newAliasId(): string {
  aliasSeq += 1;
  return `opp-sales-rep-alias-${aliasSeq}`;
}

interface OpportunitySalesRepAliasEditorProps {
  /** Whether the parent modal is open (gates the query). */
  isOpen: boolean;
}

/**
 * Maps a ConnectWise opportunity primary sales rep (a member) onto a PSA member. On the
 * opportunities sync, a CW opportunity's sales rep sets the synced opportunity's sales
 * rep through these rows; an opportunity whose sales rep has no row leaves the sales rep
 * null/unchanged (the sync never fails on a missing alias).
 */
export function OpportunitySalesRepAliasEditor({
  isOpen,
}: OpportunitySalesRepAliasEditorProps): JSX.Element {
  const configQuery = useCwOpportunitySalesRepAliases(isOpen);
  const saveAliases = useSetCwOpportunitySalesRepAliases();

  const [rows, setRows] = useState<AliasRow[]>([]);
  const config = configQuery.data;

  useEffect(() => {
    if (!config) return;
    setRows(
      config.aliases.map((alias) => ({
        id: newAliasId(),
        cwSalesRepName: alias.cwSalesRepName,
        memberId: alias.memberId,
      })),
    );
  }, [config]);

  const members = config?.members ?? [];
  const discoveredSalesReps = config?.discoveredSalesReps ?? [];
  const live = config?.live ?? false;

  const mappedNames = useMemo(
    () => new Set(rows.map((row) => row.cwSalesRepName.trim().toLowerCase()).filter(Boolean)),
    [rows],
  );
  const unmappedDiscovered = discoveredSalesReps.filter(
    (name) => !mappedNames.has(name.trim().toLowerCase()),
  );

  const updateRow = (id: string, patch: Partial<AliasRow>): void => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };
  const removeRow = (id: string): void => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };
  const addRow = (cwSalesRepName = ''): void => {
    setRows((prev) => [...prev, { id: newAliasId(), cwSalesRepName, memberId: '' }]);
  };

  const handleSave = (): void => {
    const aliases: CwOpportunitySalesRepAliasDto[] = rows
      .filter((row) => row.cwSalesRepName.trim() && row.memberId)
      .map((row) => ({
        cwSalesRepName: row.cwSalesRepName.trim(),
        memberId: row.memberId,
      }));
    saveAliases.mutate({ aliases });
  };

  const datalistId = 'cw-opportunity-sales-reps';

  return (
    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">Sales rep from ConnectWise sales rep</h3>
        <p className="mt-1 text-sm text-slate-500">
          Map each ConnectWise opportunity sales rep to a PSA member below. On sync, an
          opportunity&apos;s ConnectWise primary sales rep sets its PSA sales rep — an opportunity
          whose sales rep has no row leaves the sales rep unchanged.
        </p>
      </div>

      {configQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading members…</p>
      ) : configQuery.isError ? (
        <p className="text-sm text-rose-600">Failed to load members.</p>
      ) : (
        <>
          {live ? (
            <p className="text-xs text-emerald-600">
              ConnectWise sales reps loaded live from your connection.
            </p>
          ) : (
            <p className="text-xs text-slate-400">
              No ConnectWise sales reps discovered yet. Configure the connection to load
              suggestions — you can still type a name.
            </p>
          )}

          <datalist id={datalistId}>
            {discoveredSalesReps.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          {saveAliases.isError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              Could not save the sales-rep aliases. Make sure each row targets a valid member.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <p className="text-sm text-slate-400">No sales-rep aliases yet. Add one below.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="py-2 pr-3 font-medium">ConnectWise sales rep</th>
                  <th className="py-2 pr-3 font-medium">PSA member</th>
                  <th className="py-2 font-medium sr-only">Remove</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-2 pr-3 align-top">
                      <input
                        type="text"
                        list={datalistId}
                        value={row.cwSalesRepName}
                        placeholder="e.g. Jane Doe"
                        onChange={(e) => updateRow(row.id, { cwSalesRepName: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                    </td>
                    <td className="py-2 pr-3 align-top">
                      <select
                        value={row.memberId}
                        onChange={(e) => updateRow(row.id, { memberId: e.target.value })}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      >
                        <option value="">— Select member —</option>
                        {members.map((member) => (
                          <option key={member.id} value={member.id}>
                            {member.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 align-top">
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="mt-1.5 text-xs font-medium text-slate-400 hover:text-rose-600"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => addRow()}>
              Add alias
            </Button>
            {unmappedDiscovered.length > 0 ? (
              <div className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span>Unmapped:</span>
                {unmappedDiscovered.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => addRow(name)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-0.5 hover:border-brand-400 hover:text-brand-700"
                  >
                    {name}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="ml-auto">
              <Button onClick={handleSave} isLoading={saveAliases.isPending} disabled={!config}>
                Save sales reps
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
